<template>
  <div>
    <AliasDialog
      v-model:visibility="aliasDialogVisibility"
      :alias="aliasDialogAlias"
      :user="person"
      @refetch-user="(newPerson) => (person = newPerson)" />

    <AliasDeleteDialog
      v-if="aliasDeleteDialogVisibility && aliasDeleteDialogAlias"
      v-model:visibility="aliasDeleteDialogVisibility"
      :alias="aliasDeleteDialogAlias"
      :response="aliasDeleteDialogResponse"
      @abort="() => deleteDialogAbort()"
      @confirm="() => handleAliaseDelete(aliasDeleteDialogAlias)" />

    <div class="el-descriptions el-descriptions__title header-wrapper h-full">
      <span>{{ i18n.t('drawers.userDetailDrawer.aliasesCard.title') }}</span>
      <div>
        <SmButton
          v-require-permission="CompanyAccessRole.ADMIN"
          outline
          size="small"
          style="margin-top: 2px"
          @click="
            () => (
              (aliasDialogAlias = undefined), (aliasDialogVisibility = true)
            )
          ">
          <Plus class="mr-2" :size="18" />
          {{ i18n.t('drawers.userDetailDrawer.aliasesCard.createAlias') }}
        </SmButton>
      </div>
    </div>

    <SmTable
      :data="aliasesData"
      :columns="columns"
      :expandable="true"
      key-field="name"
      class="!h-auto overflow-hidden">
      <template #name="{ row }">
        <TextMultiline :text="row.name" />
      </template>

      <template #description="{ row }">
        <TextMultiline :text="row.description" />
      </template>

      <template #actions="{ row }">
        <SmDropdown trigger="click">
          <!-- Edit alias -->
          <SmDropdownItem
            :icon="PencilLine"
            @click="
              () => ((aliasDialogAlias = row), (aliasDialogVisibility = true))
            ">
            {{ i18n.t('editAlias') }}
          </SmDropdownItem>

          <!-- Make primary -->
          <SmDropdownItem :icon="Star" @click="makeAliasPrimary(row)">
            {{ i18n.t('makePrimaryMail') }}
          </SmDropdownItem>

          <!-- Delete -->
          <SmDropdownItem :icon="Trash" @click="showDeleteDialog(row)">
            {{ i18n.t('deleteAlias') }}
          </SmDropdownItem>
        </SmDropdown>
      </template>

      <template #expand="{ row }">
        <div
          v-if="aliasesLicences[row.name] && aliasesLicences[row.name].length"
          class="bg-gray-50 dark:bg-gray-700">
          <SmTable
            lazy
            :data="aliasesLicences[row.name]"
            :columns="expandColumns"
            key-field="_id"
            class="!h-auto overflow-hidden py-4 pl-14">
            <template #applicaction="{ row: licenseRow }">
              <div class="flex items-center gap-3">
                <ApplicationIcon
                  :application-name="getApplicationName(licenseRow.software_id)"
                  :size="20" />
                <TextMultiline
                  :text="getApplicationName(licenseRow.software_id)" />
              </div>
            </template>
            <template #sub_account="{ row: licenseRow }">
              <TextMultiline
                v-if="getSubAccountNameById(licenseRow.sub_account_id)"
                :text="getSubAccountNameById(licenseRow.sub_account_id)"
                :show-tool-tip="true" />
            </template>
            <template #licence_model="{ row: licenseRow }">
              <TextMultiline
                v-for="license in licenseRow.software_licenses"
                :key="license.name"
                :text="license.name" />

              <span v-if="!row.software_licenses">
                {{ i18n.t('general.noModel') }}
              </span>
            </template>
          </SmTable>
        </div>
      </template>
    </SmTable>
  </div>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'

  import {
    Account,
    CompanyAccessRole,
    LicenceOut,
    SoftwareOut,
    SubAccount_Output,
    UserAliasesService,
    UserLicencesService,
    UsersService,
  } from '@/client'
  import { editToast, sendToast } from '@/components/sm/SmNotification'
  import { AliasType } from '@/types/user'
  import { Column } from '../sm/SmTable.types'
  import { PersonStore } from '@/stores/person/person.store'
  import { PencilLine, Plus, Star, Trash } from 'lucide-vue-next'

  interface aliasData {
    description: string
    application_id: string | null
    sub_account_id: string | null
    name: string
  }

  const emit = defineEmits(['update:person'])
  const props = defineProps({
    person: {
      type: Object as PropType<Account>,
      required: true,
    },
    applications: {
      type: Array as PropType<SoftwareOut[]>,
      required: true,
    },
  })

  const person = computed({
    get: () => props.person,
    set: (value) => emit('update:person', value),
  })

  const applications = computed(() => props.applications)

  const i18n = useI18n()

  const aliasDialogVisibility = ref<boolean>(false)
  const aliasDialogAlias = ref<AliasType>()
  const aliasesData = computed<aliasData[]>(() => {
    if (person.value && person.value.aliases) {
      return Object.entries(person.value.aliases).map(([key, value]) => ({
        name: key,
        description: value.description,
        application_id: value.software_id || null,
        sub_account_id: value.sub_account_id || null,
      }))
    }
    return []
  })

  const columns: Column<aliasData>[] = [
    {
      key: 'name',
      label: i18n.t('drawers.userDetailDrawer.licencesCard.table.name'),
      width: 1,
    },
    {
      key: 'description',
      label: i18n.t('general.description'),
      sortable: true,
      width: 1,
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      width: 'auto',
    },
  ]

  const expandColumns: Column<LicenceOut>[] = [
    {
      key: 'applicaction',
      label: i18n.t('Application'),
      sortable: false,
      width: 1,
    },
    {
      key: 'sub_account',
      label: i18n.t('subaccount'),
      sortable: false,
      width: 1,
    },
    {
      key: 'licence_model',
      label: i18n.t('drawers.userDetailDrawer.licencesCard.table.licenceModel'),
      sortable: false,
      width: 1,
    },
  ]

  const aliasesLicences = ref<Record<string, LicenceOut[]>>({})

  const refetchPerson = async () => {
    await PersonStore.refreshPersonCacheFromApi({
      email: person.value.email,
    })
    const _person = await PersonStore.getPersonById(person.value._id)
    if (_person) person.value = _person
  }

  const aliasDeleteDialogVisibility = ref<boolean>(false)
  const aliasDeleteDialogAlias = ref<AliasType>()
  const aliasDeleteDialogResponse = ref()

  const showDeleteDialog = (row: AliasType) => {
    UserAliasesService.removeAliasApiV1ManagementUsersEmailOrIdAliasUserNameDelete(
      {
        userName: row.name,
        emailOrId: person.value.email,
        unassignLicences: true,
        preview: true,
      }
    ).then((response) => {
      aliasDeleteDialogResponse.value = response
      aliasDeleteDialogAlias.value = row
      aliasDeleteDialogVisibility.value = true
    })
  }

  const handleAliaseDelete = (row?: AliasType) => {
    if (!row) {
      return sendToast(
        i18n.t('error.request.unknown.title'),
        i18n.t('error.request.unknown.message'),
        'error'
      )
    }

    UserAliasesService.removeAliasApiV1ManagementUsersEmailOrIdAliasUserNameDelete(
      {
        userName: row.name,
        emailOrId: person.value.email,
        unassignLicences: true,
      }
    ).then(() => {
      refetchPerson()
      deleteDialogAbort()
    })
  }

  const deleteDialogAbort = () => {
    aliasDeleteDialogResponse.value = undefined
    aliasDeleteDialogAlias.value = undefined
    aliasDeleteDialogVisibility.value = false
  }

  const getSubAccountNameById = (subAccountId: string): string | undefined => {
    const subAccount = applications.value
      .flatMap((application) => {
        if (application.sub_accounts) {
          if (Array.isArray(application.sub_accounts)) {
            return application.sub_accounts
          } else {
            return Object.values(application.sub_accounts)
          }
        } else {
          return []
        }
      })
      .find((subAccount: SubAccount_Output) => subAccount.id === subAccountId)

    return subAccount?.name || undefined
  }

  async function makeAliasPrimary(row: aliasData) {
    const toastId = sendToast(
      i18n.t('notification.loading'),
      undefined,
      'loading'
    )

    UsersService.updateUserEmailApiV1ManagementUsersAccountIdEmailPost({
      accountId: person.value._id,
      requestBody: {
        email: row.name,
      },
    }).then((response) => {
      if (!response) {
        editToast(toastId, {
          title: i18n.t('notification.loadingError'),
          message: '',
          type: 'error',
        })
      } else {
        editToast(toastId, {
          title: i18n.t('successfullySaved'),
          message: '',
          type: 'success',
        })
      }

      person.value.email = row.name

      loadAliases()
      refetchPerson()
    })
  }

  function getApplicationName(id: string) {
    const application = applications.value.find(
      (application) => application._id === id
    )

    return application?.software_name || ''
  }

  function loadAliases() {
    if (person.value && person.value.aliases) {
      Object.keys(person.value.aliases || {}).forEach((alias) => {
        UserLicencesService.getLicencesApiV1SoftwareLicencesGet({
          email: alias,
        }).then((licences) => {
          // Check if the alias is still in the list
          aliasesLicences.value[alias] = licences
        })
      })
    }
  }

  onMounted(() => {
    loadAliases()
  })
</script>

<style scoped>
  .company-mini-img {
    width: 20px;
    height: 20px;
  }
  .table_row_cell_content {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
  .text-muted {
    color: var(--el-text-color-secondary);
    font-style: italic;
    gap: 0.5rem;
    display: flex;
    align-items: center;
  }
  .header-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }
  .delete-button:hover {
    color: var(--el-color-danger);
  }
</style>
